import React from 'react';
import { ButtonBase, Link, Text } from '@stitch-fix/mode-react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { ControlledTextField } from '../Forms';
import { SignInFormWrapper } from './SignInFormWrapper';
import { isAndroidApp } from '../../utils/registration/android';
import {
  loginByEmail,
  isLoginAuthError,
} from '../../utils/requests/clientAuth/login';
import { ReCaptchaWrapper } from './ReCaptchaWrapper';
import { SignInFormFooter } from './SignInFormFooter';
import { BannerWrapper } from './BannerWrapper';
import { SignInMachineContext } from '../../machines/sign-in/SignInMachineContext';
import { browserLogger } from '../../utils/loggers/browser';
import { emailSchema } from '../../utils/zod/emailSchema';
import {
  eraseHighIntentCookie,
  getHighIntentCookie,
  useDynamicProductAd,
} from '../../features/highIntent/utils/useDynamicProductAd';

const loginFormSchema = z
  .object({
    password: z
      .string({ required_error: 'Please enter your password.' })
      .max(75),
  })
  .merge(emailSchema);

type LoginFormSchema = z.infer<typeof loginFormSchema>;

export const EmailPasswordForm = () => {
  const dynamicProductAdCreate = useDynamicProductAd();
  const [state, send] = SignInMachineContext.useActor();

  const { control, handleSubmit } = useForm<LoginFormSchema>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: {},
  });

  const isWafEnabled = state.context.isWafEnabled;

  const onSubmit = async (data: LoginFormSchema) => {
    try {
      const {
        data: { redirect_url },
      } = await loginByEmail(
        data.email,
        data.password,
        state.context.recaptchaToken,
        isWafEnabled,
      );

      const dpaCookie = getHighIntentCookie();

      if (dpaCookie) {
        await dynamicProductAdCreate({
          clientType: 'login',
          variables: {
            input: {
              id: dpaCookie.id,
              grouping: dpaCookie.grouping,
            },
          },
        });

        eraseHighIntentCookie();
      }

      send({
        type: 'LOGIN_SUCCESS',
        redirectUrl: redirect_url,
      });
    } catch (error) {
      if (isLoginAuthError(error)) {
        send({
          type: 'LOGIN_FAILURE',
          error: error.response.data,
          isWafEnabled,
        });
      } else {
        let message = 'Unknown error during login';
        let stack;

        if (error instanceof Error) {
          message = error.message;
          stack = error.stack;
        }

        browserLogger({
          level: 'error',
          team: 'client-onboarding',
          message: 'Unknown error during login',
          context: { message, stack, isWafEnabled },
        });
      }
    }
  };

  return (
    <SignInFormWrapper
      onSubmit={handleSubmit(onSubmit)}
      subheading={
        !isAndroidApp() && (
          <>
            Use your password or try{' '}
            <Link
              as={ButtonBase}
              onClick={() => {
                send('PASSWORDLESS_SIGNIN');
              }}
            >
              password-less sign in.
            </Link>
          </>
        )
      }
      ctaText="Continue"
      footer={<SignInFormFooter />}
    >
      <BannerWrapper />
      <ControlledTextField
        control={control}
        autocomplete="username webauthn"
        name="email"
        label="Email"
        id="signin-email"
      />
      <ControlledTextField
        control={control}
        autocomplete="current-password"
        type="password"
        name="password"
        label="Password"
        id="signin-password"
      />
      <Text align="center" mb={2} setting="body-small">
        Forgot password? <Link href="/password/new">Reset it now</Link>
      </Text>
      {state.matches('emailPasswordSignIn.recaptchaRequired') && (
        <ReCaptchaWrapper
          onChange={token =>
            send({ type: 'SET_RECAPTCHA_TOKEN', token: token ?? '' })
          }
        />
      )}
    </SignInFormWrapper>
  );
};
